import axios from "axios";
import { useEffect, useState } from "react";
import { Alert, Col, Container, Row, Button } from "reactstrap";
import { QBProfitLossGraph } from "./ProfitLoss";
import { QBBalanceSheetGraph } from "./BalanceSheet";

interface QuickBooksDashboardProps {
    onDisconnect?: () => void;
}

export const QuickBooksDashboard = ({ onDisconnect }: QuickBooksDashboardProps) => {

    const [companyInfo, setCompanyInfo] = useState<any>(null);
    const [showloader, setshowloader] = useState(true);
    const [disconnecting, setDisconnecting] = useState(false);
    const [alertMsgg, setAlertMsgg] = useState<any>({ open: false, type: "success", content: "Messages here.", time: "2500" });
    const handleAlertMsggClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") { return; }
        setAlertMsgg({ ...alertMsgg, open: false });
    };

    const getCompanyDetails = async () => {
        try {
            setshowloader(true);
            const response = await axios.get(
                `/assignments/api/companyinfo/`,
                {},
                { withCredentials: true }
            );
            if (response && response.reconnect_required) {
                setAlertMsgg({ ...alertMsgg, open: true, content: response.error || "QuickBooks token expired. Please reconnect.", type: "error" });
            } else if (response && response.status === "SENT") {
                console.log("Company Info: ", response);
                setCompanyInfo(response.company.CompanyInfo || {});
            }
            setshowloader(false);
        } catch (error: any) {
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data?.error || "Error occurred", type: "error" });
                } else if (error.response.status === 403) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Session expired!", type: "error" });
                    setTimeout(() => { window.location.href = "/logout"; }, 2000);
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "An error occurred", type: "error" });
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    }

    const handleDisconnect = async () => {
        if (!window.confirm("Are you sure you want to disconnect QuickBooks? You will need to reconnect to access QuickBooks features.")) {
            return;
        }

        setDisconnecting(true);
        try {
            const response = await axios.post(
                "/assignments/disconnect/",
                {},
                { withCredentials: true }
            );

            if (response && response.success) {
                setAlertMsgg({
                    open: true,
                    type: "success",
                    content: response.message || "QuickBooks disconnected successfully"
                });
                
                // Notify parent component to refresh state
                if (onDisconnect) {
                    setTimeout(() => {
                        onDisconnect();
                    }, 1500);
                } else {
                    // Fallback: reload the page after a delay
                    setTimeout(() => {
                        window.location.reload();
                    }, 1500);
                }
            }
        } catch (error: any) {
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    setAlertMsgg({
                        ...alertMsgg,
                        open: true,
                        content: error.response.data?.error || "Error disconnecting QuickBooks",
                        type: "error"
                    });
                } else if (error.response.status === 403) {
                    setAlertMsgg({
                        ...alertMsgg,
                        open: true,
                        content: "Session expired!",
                        type: "error"
                    });
                    setTimeout(() => { window.location.href = "/logout"; }, 2000);
                } else {
                    setAlertMsgg({
                        ...alertMsgg,
                        open: true,
                        content: error.response.data?.error || "Failed to disconnect QuickBooks",
                        type: "error"
                    });
                }
            } else {
                setAlertMsgg({
                    ...alertMsgg,
                    open: true,
                    content: "Network error",
                    type: "error"
                });
            }
        } finally {
            setDisconnecting(false);
        }
    };

    useEffect(() => {
        getCompanyDetails();
    }, []);

    return <Container fluid>
        {alertMsgg.open && (
            <Alert
                color={alertMsgg.type === "success" ? "success" : "danger"}
                toggle={handleAlertMsggClose}
                fade={false}
                style={{ fontWeight: "bold" }}
            >
                {alertMsgg.content}
            </Alert>
        )}
        <div className="loaderContainer" hidden={!showloader && !disconnecting}>
            <div className="spinner">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
            </div>
        </div>
        <Row className="mb-3">
            <Col xl={12} className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0"><b>Company</b>: {companyInfo && companyInfo.CompanyName}</h6>
                <Button
                    color="danger"
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    size="sm"
                >
                    {disconnecting ? "Disconnecting..." : "Disconnect QuickBooks"}
                </Button>
            </Col>
        </Row>
        <Row>
            <Col xl={6}><QBProfitLossGraph alertMsgg={alertMsgg} setAlertMsgg={setAlertMsgg} setshowloader={setshowloader} /> </Col>
            <Col xl={6}><QBBalanceSheetGraph alertMsgg={alertMsgg} setAlertMsgg={setAlertMsgg} setshowloader={setshowloader} /> </Col>
        </Row>
    </Container>
}